<template>
    <div id="mci-app-tutorials">
        <div v-if="error" class="rt-state rt-state--error">
            Sorry — tutorials couldn't be loaded right now. Please try again later.
        </div>

        <div v-else-if="!loaded" class="rt-state">Loading tutorials…</div>

        <template v-else>
            <div v-if="allTags.length" class="rt-filters">
                <button
                    type="button"
                    class="rt-chip"
                    :class="{ 'rt-chip--active': !activeTags.length }"
                    @click="clearFilters"
                >
                    All
                </button>
                <button
                    v-for="tag in allTags"
                    :key="tag"
                    type="button"
                    class="rt-chip"
                    :class="{ 'rt-chip--active': activeTags.includes(tag) }"
                    @click="toggleTag(tag)"
                >
                    {{ tag }}
                </button>
            </div>

            <div v-if="filtered.length" class="rt-grid">
                <div v-for="tutorial in filtered" :key="tutorial.id" class="rt-card">
                    <div class="rt-card__video">
                        <iframe
                            :src="embedUrl(tutorial)"
                            :title="tutorial.title"
                            frameborder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowfullscreen
                            loading="lazy"
                        ></iframe>
                    </div>
                    <h3 class="rt-card__title">{{ tutorial.title }}</h3>
                    <p v-if="tutorial.description" class="rt-card__desc">{{ tutorial.description }}</p>
                </div>
            </div>

            <p v-else class="rt-state">No tutorials to show.</p>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import useApi from '@/composables/useApi.js';

const node = inject('node', null);
const { loadTutorials } = useApi();

const tutorials = ref([]);
const loaded = ref(false);
const error = ref(false);
const activeTags = ref([]);

// Optional per-embed surface override: <div class="rabbit-idx-node-tutorials" data-surface="website">
const surface = node?.dataset?.surface || 'website';

onMounted(async () => {
    try {
        tutorials.value = await loadTutorials(surface);
    } catch (e) {
        error.value = true;
    } finally {
        loaded.value = true;
    }
});

////////////////////////////////////////////////////////////////////
/// Tag vocabulary derived from the loaded set
const allTags = computed(() => {
    const set = new Set();
    tutorials.value.forEach((tutorial) => (tutorial.tags || []).forEach((tag) => set.add(tag)));
    return [...set].sort();
});

////////////////////////////////////////////////////////////////////
/// OR filter — a tutorial matches if it has any active tag
const toggleTag = (tag) => {
    const index = activeTags.value.indexOf(tag);
    if (index === -1) {
        activeTags.value.push(tag);
    } else {
        activeTags.value.splice(index, 1);
    }
};

const clearFilters = () => {
    activeTags.value = [];
};

const filtered = computed(() => {
    if (!activeTags.value.length) {
        return tutorials.value;
    }
    return tutorials.value.filter((tutorial) =>
        (tutorial.tags || []).some((tag) => activeTags.value.includes(tag)),
    );
});

////////////////////////////////////////////////////////////////////
///
const embedUrl = (tutorial) => {
    return `https://player.vimeo.com/video/${tutorial.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
};
</script>
